import { AdminLevel } from '../map/LocationMapTypes'              // adjust path
import { LevelsData } from '../../context/LocationDataContext'    // adjust path
import { AreaRow, UkAdminType } from './AreaTableType'

// Wards are in the thousands, so keep level 4 out unless you really need it.
export const TABLE_LEVELS: AdminLevel[] = [1, 2, 3]

// used only when a feature has no type property
const DEFAULT_TYPE: Record<number, UkAdminType> = {
    1: 'Country',
    2: 'County',
    3: 'District',
    4: 'Ward',
}

// property names tried in order. Add your real keys here.
const NAME_KEYS = ['name', 'NAME', 'shapeName', 'NAME_1', 'NAME_2', 'NAME_3', 'NAME_4']
const TYPE_KEYS = ['type', 'TYPE', 'admin_type', 'adminType', 'level_type', 'designation']
const CODE_KEYS = ['code', 'CODE', 'id', 'ID', 'gss', 'GSS', 'GSS_CODE', 'shapeID', 'ONS_CODE']
const PARENT_KEYS = ['parent', 'PARENT', 'parent_id', 'parentId', 'parent_code', 'PARENT_CODE', 'parentCode']

const pick = (props: Record<string, any> | null | undefined, keys: string[]): string | undefined => {
    if (!props) return undefined
    for (const key of keys) {
        if (props[key]) return String(props[key])
    }
    return undefined
}

/* ---------- small geometry helpers (no libraries) ---------- */

type Pt = [number, number]
type Poly = number[][][]   // rings: [outer, ...holes]
type BBox = [number, number, number, number]   // minX, minY, maxX, maxY

const polysOf = (geom: any): Poly[] => {
    if (!geom) return []
    if (geom.type === 'Polygon') return [geom.coordinates]
    if (geom.type === 'MultiPolygon') return geom.coordinates
    return []
}

const bboxOf = (polys: Poly[]): BBox => {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
    for (const poly of polys) {
        for (const [x, y] of poly[0]) {
            if (x < minX) minX = x
            if (y < minY) minY = y
            if (x > maxX) maxX = x
            if (y > maxY) maxY = y
        }
    }
    return [minX, minY, maxX, maxY]
}

const inRing = (p: Pt, ring: number[][]): boolean => {
    let inside = false
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const [xi, yi] = ring[i]
        const [xj, yj] = ring[j]
        if ((yi > p[1]) !== (yj > p[1]) && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) {
            inside = !inside
        }
    }
    return inside
}

const inPoly = (p: Pt, poly: Poly): boolean =>
    inRing(p, poly[0]) && !poly.slice(1).some((hole) => inRing(p, hole))

// about a dozen points taken around the boundary of the biggest polygon
const samplePoints = (polys: Poly[]): Pt[] => {
    if (!polys.length) return []
    const biggest = polys.reduce((a, b) => (b[0].length > a[0].length ? b : a))
    const ring = biggest[0]
    const step = Math.max(1, Math.floor(ring.length / 12))
    const pts: Pt[] = []
    for (let i = 0; i < ring.length; i += step) pts.push([ring[i][0], ring[i][1]])
    return pts
}

/* ---------- tree building ---------- */

interface Node {
    row: AreaRow
    code?: string
    parentCode?: string
    polys: Poly[]
    bbox: BBox
    children: Node[]
}

const byName = (a: Node, b: Node) => a.row.name.localeCompare(b.row.name)

export const buildRows = (data: LevelsData): AreaRow[] => {
    // 1. one node per feature
    const nodesByLevel = new Map<number, Node[]>()
    const codeMap = new Map<string, Node>()

    TABLE_LEVELS.forEach((level) => {
        const nodes = (data[level]?.features ?? []).map((feature: any, index: number): Node => {
            const polys = polysOf(feature.geometry)
            const code = pick(feature.properties, CODE_KEYS) ?? (feature.id != null ? String(feature.id) : undefined)

            const node: Node = {
                row: {
                    id: `${level}-${feature.id ?? feature.properties?.id ?? index}`,
                    code: code ?? '',
                    parentId: null,
                    depth: 0,
                    name: pick(feature.properties, NAME_KEYS) ?? 'Unknown',
                    level: pick(feature.properties, TYPE_KEYS) ?? DEFAULT_TYPE[level],
                    levelNo: level,
                    previousAlert: 'None',
                    newAlert: 'None',
                    rainfall: 0,
                    minWindSpeed: 0,
                    maxWindSpeed: 0,
                    minWaveHeight: 0,
                    maxWaveHeight: 0,
                    seaCondition: 'None',
                    lightning: 'None',
                    startDate: '',
                    endDate: '',
                    selected: false,
                    continued: false,
                } as AreaRow,
                code,
                parentCode: pick(feature.properties, PARENT_KEYS),
                polys,
                bbox: polys.length ? bboxOf(polys) : [0, 0, 0, 0],
                children: [],
            }

            if (code) codeMap.set(code, node)
            return node
        })
        nodesByLevel.set(level, nodes)
    })

    // 2. find each node's parent
    const roots: Node[] = []

    TABLE_LEVELS.forEach((level) => {
        const upperLevels = TABLE_LEVELS.filter((l) => l < level).reverse()   // nearest level first

        ;(nodesByLevel.get(level) ?? []).forEach((node) => {
            let parent: Node | undefined

            // a) by parent code
            if (node.parentCode) {
                const byCode = codeMap.get(node.parentCode)
                if (byCode && byCode.row.levelNo < level) parent = byCode
            }

            // b) by map position: the parent polygon that holds most of the child's boundary points
            if (!parent && node.polys.length) {
                const points = samplePoints(node.polys)

                for (const pl of upperLevels) {
                    let best: Node | undefined
                    let bestScore = 0

                    for (const candidate of nodesByLevel.get(pl) ?? []) {
                        const [minX, minY, maxX, maxY] = candidate.bbox
                        let score = 0
                        for (const p of points) {
                            if (p[0] < minX || p[0] > maxX || p[1] < minY || p[1] > maxY) continue
                            if (candidate.polys.some((poly) => inPoly(p, poly))) score++
                        }
                        if (score > bestScore) {
                            bestScore = score
                            best = candidate
                        }
                    }

                    if (best) {
                        parent = best
                        break
                    }
                }
            }

            if (parent) {
                node.row.parentId = parent.row.id
                parent.children.push(node)
            } else {
                roots.push(node)
            }
        })
    })

    // 3. walk the tree: parent first, then its children, then their children
    const result: AreaRow[] = []

    const walk = (node: Node, depth: number) => {
        node.row.depth = depth
        result.push(node.row)
        node.children.sort(byName).forEach((child) => walk(child, depth + 1))
    }

    roots
        .sort((a, b) => a.row.levelNo - b.row.levelNo || byName(a, b))
        .forEach((root) => walk(root, 0))

    return result
}